import React from "react";

import { toast } from "sonner";

interface ToasterBoundaryState {
  broken: boolean;
  generation: number;
}

/**
 * Contém erros de render vindos de toasts — ex.: `toast.error(objeto)` com um
 * corpo `{ error, stack }` dos 500 do api-v2, que o React não consegue
 * renderizar como filho (erro minificado #31).
 *
 * Sem esta barreira, o crash sobe até o ErrorBoundary global do `_app` e
 * derruba a aplicação inteira para "Erro da Aplicação" (incidente 2026-07-14
 * ao clicar em "Manter plano" na aba de planos). Um toast é um aviso
 * descartável; falhar nele não pode ter o mesmo raio de explosão do app.
 *
 * Recuperação: descarta a fila do sonner (o toast envenenado vive num store
 * fora da árvore React e voltaria a quebrar no remount) e remonta o Toaster
 * vazio via bump de `key`.
 */
export class ToasterBoundary extends React.Component<{ children: React.ReactNode }, ToasterBoundaryState> {
  override state: ToasterBoundaryState = { broken: false, generation: 0 };

  static getDerivedStateFromError(): Partial<ToasterBoundaryState> {
    return { broken: true };
  }

  override componentDidCatch(error: Error) {
    console.error("Toast com conteúdo não renderizável contido pelo ToasterBoundary:", error);
    toast.dismiss();
    this.setState(state => ({ broken: false, generation: state.generation + 1 }));
  }

  override render() {
    if (this.state.broken) {
      return null;
    }
    return <React.Fragment key={this.state.generation}>{this.props.children}</React.Fragment>;
  }
}
